import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import LocationQuestion from './Quiz/LocationQuestion';
import MarbleheadPricing from './Quiz/MarbleheadPricing';
import ResultScreen from './Quiz/ResultScreen';
import OrderForm from './Order/OrderForm';
import OrderSuccess from './Order/OrderSuccess';
import ShareButton from './ShareButton';

export default function Hero() {
  const [isFlowOpen, setIsFlowOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<'purchase-type' | 'roast-selection' | 'location' | 'marblehead-pricing' | 'result' | 'order' | 'success'>('purchase-type');
  const [isMarblehead, setIsMarblehead] = useState(false);
  const [selectedTier, setSelectedTier] = useState<any>(null);
  const [customerName, setCustomerName] = useState('');
  const [selectedRoast, setSelectedRoast] = useState<string>('');
  const [purchaseType, setPurchaseType] = useState<'monthly' | 'single'>('monthly');

  const handleLocationSelect = (marbleheadResident: boolean) => {
    setIsMarblehead(marbleheadResident);
    if (marbleheadResident && purchaseType === 'monthly') {
      setCurrentStep('marblehead-pricing');
    } else if (marbleheadResident && purchaseType === 'single') {
      // For single orders in Marblehead, create a simple tier and go straight to order
      const simpleTier = {
        id: 'marblehead-single',
        tier_name: 'Marblehead Single Order',
        monthly_price: 2200, // $22.00
        selectedRoastType: selectedRoast,
        purchaseType: 'single',
        isMarblehead: true
      };
      setSelectedTier(simpleTier);
      setCurrentStep('order');
    } else {
      setCurrentStep('result');
    }
  };

  const handleProceedToOrder = (tier: any) => {
    setSelectedTier(tier);
    setCurrentStep('order');
  };

  const handleOrderSuccess = (name: string, isMarblehead: boolean, selectedTier: any) => {
    setCustomerName(name);
    setCurrentStep('success');
  };

  const handleClose = () => {
    setIsFlowOpen(false);
    setCurrentStep('purchase-type');
    setIsMarblehead(false);
    setSelectedTier(null);
    setCustomerName('');
    setSelectedRoast('');
    setPurchaseType('monthly');
  };

  const handlePurchaseTypeSelect = (type: 'monthly' | 'single') => {
    setPurchaseType(type);
    if (type === 'monthly') {
      setCurrentStep('location');
    } else {
      setCurrentStep('roast-selection');
    }
  };

  const handleRoastSelect = (roastId: string) => {
    setSelectedRoast(roastId);
    setCurrentStep('location');
  };

  const roasts = [
    {
      id: 'hammer-fist',
      name: 'Hammer Fist',
      description: "A blend for friends who've seen everything. Guatemala, Ethiopia, and Tanzania — three very different origins, better when they come together. Named for a self-defense move that never stopped being funny.",
      image: '/ChatGPT_Image_Aug_18,_2026,_09_43_45_AM.png',
      emoji: '🤜',
      price: 19,
      size: '12 oz',
      tastingNotes: ['Rich', 'Fruity', 'Complex', 'Friendship'],
      isGiftBox: false,
      linkToPage: '/hammer-fist',
      isSoldOut: false
    },
    {
      id: 'kilted-and-caffeinated',
      name: 'Kilted & Caffeinated',
      description: 'Limited edition. Not beer. For the Tartan Army, Boston bartenders, and anyone who needs to be upright before the match. A bond of Scottish heritage and local enthusiasm.',
      image: '/ChatGPT_Image_Jun_18,_2026_at_01_37_06_PM.png',
      emoji: '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
      price: 27,
      size: '10 oz',
      tastingNotes: ['Limited Edition', 'Not Beer', 'Bold', 'Caffeinated'],
      isGiftBox: false,
      linkToPage: '/kilted-and-caffeinated',
      isSoldOut: false
    },
    {
      id: 'festival-blend',
      name: 'Festival Blend',
      description: 'Created in collaboration with the Marblehead Festival of Arts. Smooth, balanced, and crafted for gathering. $25 from every bag goes directly to the Festival of Arts.',
      image: '/ChatGPT_Image_May_20,_2026_at_10_36_49_AM.png',
      emoji: '🎨',
      price: 40,
      size: '10 oz',
      tastingNotes: ['Warm', 'Balanced', 'Smooth', 'Community'],
      isGiftBox: false,
      linkToPage: '/festival-blend',
      isSoldOut: false
    },
    {
      id: 'regiment-roast',
      name: 'Regiment Roast',
      description: 'Community. Craft. Showing up. A full-bodied medium roast meant for early mornings, shared tables, and the quiet moments before the day gets moving.',
      image: '/9e11bedb-5c1b-4989-b3be-9d545225578d.png',
      emoji: '⚓',
      price: 19,
      size: '12 oz',
      tastingNotes: ['Full-Bodied', 'Community', 'Heritage', 'Craft'],
      isGiftBox: false,
      linkToPage: '/regiment-roast',
      isSoldOut: false,
      soldOutMessage: "We're busy roasting — back soon!"
    },
    {
      id: 'wake-with-drake',
      name: 'Wake with Drake',
      description: 'For football mornings and family traditions. Bold, smooth, and dependable — the kind of cup you want before kickoff, school drop-off, or game day.',
      image: '/Drake2.png',
      emoji: '🏈',
      price: 19,
      size: '12 oz',
      tastingNotes: ['Bold', 'Smooth', 'Dependable', 'Game Day'],
      isGiftBox: false,
      linkToPage: '/wake-with-drake',
      isSoldOut: true
    },
    {
      id: 'header-holiday',
      name: 'Header Holiday',
      description: 'A partnership with SPUR to support families in our community. Complete gift box with 12oz coffee, commemorative bamboo spoon, and story card. Your purchase helps provide essential resources and joy to those who need it most.',
      image: '/Header Holiday Bag Label (1) copy.png',
      emoji: '🎁',
      price: 42.50,
      size: 'Gift Box',
      tastingNotes: ['Chocolate', 'Caramel', 'Smooth', 'Festive'],
      isGiftBox: true,
      linkToPage: '/header-holiday',
      isSoldOut: true
    },
    {
      id: 'barnegat-breakfast',
      name: 'Barnegat Breakfast',
      description: 'Our signature morning blend, named after the place where Marblehead\'s story began. Rich, dependable, and deeply satisfying with notes of chocolate, honey, and citrus.',
      image: 'https://i.ibb.co/Kj3zbtXd/Barnegat-Breakfast-4-x-6-in.png',
      emoji: '🌅',
      price: 21,
      size: '12 oz',
      tastingNotes: ['Chocolate', 'Honey', 'Citrus', 'Nutty']
    },
    {
      id: 'soleless-shakedown',
      name: 'Soleless Shakedown',
      description: 'Born from the iconic Hocus Pocus scene on Old Burial Hill. Bold, smooth, and mischievous with cocoa, roasted nuts, and a whisper of smoke.',
      image: 'https://i.ibb.co/yB83W3DB/Soleless-Shakedown-Final.png',
      emoji: '⚡',
      price: 21,
      size: '12 oz',
      tastingNotes: ['Cocoa', 'Roasted Nuts', 'Smoke', 'Bold'],
      isSoldOut: true
    },
    {
      id: 'nunnepog-fog',
      name: 'Nunnepog Fog Espresso Blend',
      description: 'Designed for espresso lovers, perfect for any brew. The world can wait. Pour the quiet. Crafted for depth and balance, it shines in any form — pour-over, drip, or French press.',
      image: 'https://i.ibb.co/xtC0KDLt/Nunnepog-Fog-4-x-6-in-1.png',
      emoji: '🌫️',
      price: 21,
      size: '12 oz',
      tastingNotes: ['Espresso Perfect', 'Honey', 'Hazelnut', 'Gentle']
    },
    {
      id: 'bonus-batch',
      name: 'Bonus Batch',
      description: 'Every week we end up with a little extra coffee from roasting. Still fresh. Still great. Just not enough to become a full batch. Different coffee. Different amount. No guarantees. Just a small batch of very good coffee that got lucky.',
      image: '/Bonus_Batch_(1).png',
      emoji: '✨',
      price: 8,
      size: '4 oz',
      tastingNotes: ['Fresh', 'Surprising', 'Limited', 'Lucky'],
      isGiftBox: false,
      linkToPage: '/bonus-batch',
      isSoldOut: false,
      isMarbleheadOnly: true
    },
    {
      id: 'mff-impact',
      name: 'Coffee & Community',
      description: 'A limited-release roast supporting the Marblehead Family Fund. $10 from every bag helps build safe, inclusive places for Marblehead families to play, gather and grow.',
      image: '/MHD_Family_Fun copy 2.png',
      emoji: '🤝',
      price: 25,
      size: '10 oz',
      tastingNotes: ['Local', 'Community', 'Limited', 'Impact'],
      isGiftBox: false,
      linkToPage: '/mff-impact',
      stripeUrl: 'https://buy.stripe.com/bJecN6gubbdd9815VE3VC0t',
      isSoldOut: false
    },
    {
      id: 'redds',
      name: 'Redd\'s',
      description: 'A dark roast with a darker history. Dark chocolate, molasses, and toasted caramel — small-batch roasted in Marblehead, a few streets from Redd\'s Pond and Old Burial Hill. Scan the bag and follow the true Marblehead story behind the coffee.',
      image: '/ChatGPT_Image_Sep_15,_2026,_09_56_23_AM.png',
      emoji: '🕯️',
      price: 22,
      size: '12 oz',
      tastingNotes: ['Dark Chocolate', 'Molasses', 'Toasted Caramel', 'Dark Roast'],
      isGiftBox: false,
      linkToPage: '/redds',
      isSoldOut: false
    }
  ];

  return (
    <>
      <section className="min-h-screen flex items-center relative overflow-hidden">
      <div 
        className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1447933601403-0c6688de566e?auto=format&fit=crop&q=100')] 
        opacity-60 bg-cover bg-center bg-fixed transform scale-105" 
        style={{ 
          filter: 'contrast(1.4) brightness(1.1) saturate(1.4)',
          transition: 'transform 0.5s ease-out'
        }}
      />

      <div className="container mx-auto px-4 -mt-48 md:-mt-32 relative z-10">
        <div className="max-w-4xl mx-auto text-center pt-40 md:pt-32">
          <h1 className="text-5xl md:text-8xl font-bold mb-6 md:mb-12 leading-tight md:leading-tight tracking-tight text-white">
            <span className="text-white inline">Coffee That Brings </span>
            <span className="text-[#fda36b] inline">People</span>
            <span className="block text-white">Together</span>
          </h1>
          
          <p className="text-lg md:text-xl mb-8 md:mb-12 leading-relaxed max-w-2xl mx-auto text-white">
            A little bougie. A lot welcoming.<br />
            Lovingly overthought and tested on friends in Marblehead, MA.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <button
              onClick={() => setIsFlowOpen(true)}
              data-shop-trigger
              className="group inline-flex items-center gap-2 bg-[#fda36b] text-[#132947] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#fda36b]/90 transition-all duration-300 transform hover:scale-105"
            >
              Shop
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          
          {/* Lindsey's Quote - Fun highlight */}
          <div className="mt-8 max-w-md mx-auto">
            <div className="bg-gradient-to-r from-[#fda36b]/20 via-[#fda36b]/10 to-[#fda36b]/20 backdrop-blur-sm p-4 rounded-xl border border-[#fda36b]/30 relative">
              <div className="absolute -top-2 -right-2 bg-[#fda36b] text-[#132947] px-2 py-1 rounded-full text-xs font-bold animate-pulse">
                Be like Lindsey! ☕
              </div>
              <p className="text-white/90 text-sm italic text-center leading-relaxed">
                "It's fu**ing awesome, it's so good, it's the pork. Wow this is delicious, it's everything I've ever wanted it to be. That's a perfect cup of coffee."
              </p>
              <p className="text-[#fda36b] text-xs text-center mt-2 font-semibold">
                - Lindsey, Coffee Enthusiast & Professional Pork Referencer
              </p>
            </div>
          </div>
          
          {/* Secondary Actions - Below Quote */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6">
            <ShareButton variant="secondary" size="medium" />
            
            <a 
              href="#mission"
              className="inline-flex items-center gap-2 text-white hover:text-[#fda36b] transition-colors duration-300"
            >
              Discover Our Story
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
      </section>
      
      {isFlowOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="relative bg-[#132947] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-lg transition-colors z-10"
            >
              <span className="text-white text-2xl font-bold leading-none">✕</span>
            </button>

            <div className="p-8">
              {currentStep === 'purchase-type' && (
                <div className="space-y-8">
                  <div className="text-center space-y-4">
                    <div className="text-6xl mb-4">☕</div>
                    <h2 className="text-3xl font-bold text-white mb-4">
                      What Are You Looking For?
                    </h2>
                    <p className="text-lg text-white/80">
                      Monthly variety or a specific roast?
                    </p>
                  </div>

                  <div className="space-y-6 max-w-md mx-auto">
                    <button
                      onClick={() => handlePurchaseTypeSelect('monthly')}
                      className="w-full group bg-gradient-to-r from-[#fda36b] to-[#fda36b]/80 text-[#132947] p-6 rounded-2xl font-semibold text-lg hover:from-[#fda36b]/90 hover:to-[#fda36b]/70 transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-left">
                          <div className="text-xl font-bold mb-1">Monthly Delivery</div>
                          <div className="text-sm opacity-80">Get a variety of our roasts delivered monthly</div>
                          <div className="text-xs opacity-70 mt-1">Cancel anytime • No commitment</div>
                        </div>
                        <div className="text-3xl">📦</div>
                      </div>
                    </button>

                    <button
                      onClick={() => handlePurchaseTypeSelect('single')}
                      className="w-full group bg-white/10 text-white p-6 rounded-2xl font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-[#fda36b]/30"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-left">
                          <div className="text-xl font-bold mb-1">Single Order</div>
                          <div className="text-sm opacity-80">Choose a specific blend for one-time purchase</div>
                        </div>
                        <div className="text-3xl">☕</div>
                      </div>
                    </button>

                    <a
                      href="/gift-certificates"
                      className="w-full group bg-white/10 text-white p-6 rounded-2xl font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-[#fda36b]/30 block"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-left">
                          <div className="text-xl font-bold mb-1">Gift Certificates</div>
                          <div className="text-sm opacity-80">3-month subscription: 1 bag delivered monthly</div>
                        </div>
                        <div className="text-3xl">🎁</div>
                      </div>
                    </a>
                  </div>

                  <div className="bg-white/5 p-4 rounded-lg border border-white/10 max-w-2xl mx-auto">
                    <p className="text-white/80 text-sm text-center leading-relaxed">
                      <span className="font-semibold text-[#fda36b]">Monthly Delivery:</span> We'll send different blends each month to keep things interesting! Cancel anytime, no commitment.<br />
                      <span className="font-semibold text-[#fda36b]">Single Order:</span> Perfect for trying something new or getting more of a favorite.
                    </p>
                  </div>
                </div>
              )}

              {currentStep === 'roast-selection' && (
                <div className="space-y-8">
                  <div className="text-center space-y-4">
                    <div className="text-6xl mb-4">☕</div>
                    <h2 className="text-3xl font-bold text-white mb-4">
                      Choose Your Perfect Roast
                    </h2>
                    <p className="text-lg text-white/80">
                      Select from our current lineup of carefully crafted blends
                    </p>
                  </div>

                  <div className="grid gap-6 max-w-4xl mx-auto">
                    {roasts.map((roast) => (
                      <div key={roast.id} className="bg-gradient-to-br from-white/10 to-white/5 p-4 md:p-6 rounded-2xl border border-white/10 hover:border-[#fda36b]/30 transition-all duration-300">
                        <div className="flex flex-col md:grid md:grid-cols-3 gap-4 md:gap-6 items-center">
                          {/* Product Image */}
                          <div className="w-full max-w-[200px] md:max-w-none md:col-span-1 mx-auto md:mx-0">
                            <div className="aspect-[3/4] bg-white/5 rounded-xl overflow-hidden flex items-center justify-center p-1 md:p-2">
                              <img 
                                src={roast.image} 
                                alt={roast.name}
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          </div>

                          {/* Product Info */}
                          <div className="w-full md:col-span-2 space-y-3 md:space-y-4 text-center md:text-left">
                            <div>
                              <div className="flex items-center justify-center md:justify-start gap-3 mb-2 flex-wrap">
                                <span className="text-3xl">{roast.emoji}</span>
                                <h3 className="text-xl md:text-2xl font-bold text-white">{roast.name}</h3>
                                {roast.isSoldOut && (
                                  <span className="bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-sm font-bold border border-amber-500/40">
                                    {roast.soldOutMessage ?? 'SOLD OUT'}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm md:text-base text-white/80 leading-relaxed mb-3 md:mb-4">
                                {roast.description}
                              </p>
                            </div>

                            {/* Tasting Notes */}
                            <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-3 md:mb-4">
                              {roast.tastingNotes.map((note, index) => (
                                <span key={index} className="bg-[#fda36b]/20 text-[#fda36b] px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                                  {note}
                                </span>
                              ))}
                            </div>

                            {/* Price and Size */}
                            {roast.isGiftBox ? (
                              <div className="flex flex-col items-center gap-4">
                                <div className="text-white/90 text-center">
                                  <div>
                                    <span className="text-xl md:text-2xl font-bold text-[#fda36b]">${roast.price}</span>
                                    <span className="text-white/70 ml-2 text-sm md:text-base">{roast.size}</span>
                                  </div>
                                  {roast.isSoldOut && (
                                    <div className="mt-2">
                                      <span className="text-white/50 text-sm">Sold Out</span>
                                    </div>
                                  )}
                                </div>

                                <div className="flex flex-col sm:flex-row gap-2 md:gap-3 w-full">
                                  <a
                                    href={roast.linkToPage}
                                    className="bg-white/10 text-white px-3 md:px-4 py-2 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300 text-sm text-center"
                                  >
                                    Learn More
                                  </a>
                                  {roast.isSoldOut ? (
                                    <button
                                      disabled
                                      className="bg-white/10 text-white/50 px-4 md:px-6 py-2 rounded-lg font-semibold cursor-not-allowed text-center"
                                    >
                                      {roast.soldOutMessage ?? 'Sold Out'}
                                    </button>
                                  ) : (
                                    <a
                                      href={roast.linkToPage}
                                      className="bg-[#fda36b] text-[#132947] px-4 md:px-6 py-2 rounded-lg font-semibold hover:bg-[#fda36b]/90 transition-all duration-300 text-center"
                                    >
                                      Order Now
                                    </a>
                                  )}
                                </div>
                              </div>
                            ) : roast.linkToPage ? (
                              <div className="flex flex-col md:flex-row items-center md:justify-between gap-4">
                                <div className="text-white/90 space-y-1 text-center md:text-left">
                                  <div>
                                    <span className="text-xl md:text-2xl font-bold text-[#fda36b]">${roast.price}</span>
                                    <span className="text-white/70 ml-2 text-sm md:text-base">per {roast.size} bag</span>
                                  </div>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-2 md:gap-3 w-full md:w-auto">
                                  <a
                                    href={roast.linkToPage}
                                    className="bg-white/10 text-white px-3 md:px-4 py-2 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300 text-sm text-center"
                                  >
                                    Learn More
                                  </a>
                                  {roast.isSoldOut ? (
                                    <button
                                      disabled
                                      className="bg-white/10 text-white/50 px-4 md:px-6 py-2 rounded-lg font-semibold cursor-not-allowed text-center flex items-center justify-center gap-2"
                                    >
                                      {roast.soldOutMessage ?? 'Sold Out'}
                                    </button>
                                  ) : (
                                    <a
                                      href={roast.stripeUrl ?? roast.linkToPage}
                                      target={roast.stripeUrl ? '_blank' : undefined}
                                      rel={roast.stripeUrl ? 'noopener noreferrer' : undefined}
                                      className="bg-[#fda36b] text-[#132947] px-4 md:px-6 py-2 rounded-lg font-semibold hover:bg-[#fda36b]/90 transition-all duration-300 text-center"
                                    >
                                      Order Now
                                    </a>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col md:flex-row items-center md:justify-between gap-4">
                                <div className="text-white/90 space-y-1 text-center md:text-left">
                                  <div>
                                    <span className="text-xl md:text-2xl font-bold text-[#fda36b]">As low as $16</span>
                                    <span className="text-white/70 ml-2 text-sm md:text-base">per {roast.size} bag</span>
                                  </div>
                                  <div>
                                    <button
                                      onClick={() => {
                                        setIsMarblehead(true);
                                        setCurrentStep('marblehead-pricing');
                                      }}
                                    className="text-[#fda36b] hover:text-[#fda36b]/80 text-xs md:text-sm underline transition-colors"
                                    >
                                      Learn more about our community pricing →
                                    </button>
                                  </div>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-2 md:gap-3 w-full md:w-auto">
                                  <a
                                    href={`/rateyouroast/${roast.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-white/10 text-white px-3 md:px-4 py-2 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300 text-sm text-center"
                                  >
                                    Learn More
                                  </a>
                                  {roast.isSoldOut ? (
                                    <button
                                      disabled
                                      className="bg-white/10 text-white/50 px-4 md:px-6 py-2 rounded-lg font-semibold cursor-not-allowed text-center flex items-center justify-center gap-2"
                                    >
                                      {roast.soldOutMessage ?? 'Sold Out'}
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleRoastSelect(roast.id)}
                                      className="bg-[#fda36b] text-[#132947] px-4 md:px-6 py-2 rounded-lg font-semibold hover:bg-[#fda36b]/90 transition-all duration-300 text-center"
                                    >
                                      Order This Roast
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {currentStep === 'location' && (
                <div>
                  <button
                    onClick={() => purchaseType === 'single' ? setCurrentStep('roast-selection') : setCurrentStep('purchase-type')}
                    className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8"
                  >
                    <span>←</span>
                    {purchaseType === 'single' ? 'Back to Roast Selection' : 'Back to Purchase Type'}
                  </button>
                <LocationQuestion onLocationSelect={handleLocationSelect} />
                </div>
              )}

              {currentStep === 'marblehead-pricing' && (
                <MarbleheadPricing
                  roastType={selectedRoast || "medium"}
                  email="guest@bondcoffee.com"
                  purchaseType={purchaseType}
                  selectedRoast={selectedRoast}
                  onProceedToOrder={handleProceedToOrder}
                  onBack={() => setCurrentStep('location')}
                />
              )}

              {currentStep === 'result' && (
                <ResultScreen
                  roastType={selectedRoast || "medium"}
                  email="guest@bondcoffee.com"
                  skippedQuiz={true}
                  isMarblehead={isMarblehead}
                  selectedTier={selectedTier}
                  purchaseType={purchaseType}
                  selectedRoast={selectedRoast}
                  onReset={() => setCurrentStep('location')}
                  onClose={handleClose}
                />
              )}

              {currentStep === 'order' && selectedTier && (
                <OrderForm
                  roastType={selectedTier.selectedRoastType || selectedRoast || 'medium'}
                  purchaseType={selectedTier.purchaseType || purchaseType}
                  selectedOption={selectedTier}
                  email="guest@bondcoffee.com"
                  selectedBags={selectedTier.selectedBags || 1}
                  quizSkipped={true}
                  isMarblehead={isMarblehead}
                  selectedTier={selectedTier}
                  onSuccess={handleOrderSuccess}
                  onBack={() => isMarblehead ? setCurrentStep('marblehead-pricing') : setCurrentStep('result')}
                />
              )}

              {currentStep === 'success' && (
                <OrderSuccess
                  purchaseType={selectedTier?.purchaseType || purchaseType}
                  roastType={selectedTier?.selectedRoastType || selectedRoast || 'medium'}
                  customerName={customerName}
                  isMarblehead={isMarblehead}
                  selectedTier={selectedTier}
                  onClose={handleClose}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}